import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import { parseISOToDate } from "@/lib/date-utils.ts";
import { Button } from "@/components/ui/button.tsx";
import type { ContactDropdownPosition } from "@/components/ui/contact-autocomplete.tsx";
import { CalendarAvailabilityPicker } from "@/components/scheduling/CalendarAvailabilityPicker.tsx";
import { shouldSkipStep } from "@/lib/typeahead-state-machine.ts";
import { cn } from "@/lib/utils.ts";
import { GUIDED_STEPS, STEP_LABELS } from "./types.ts";
import type { UseGuidedFlowReturn } from "./useGuidedFlow.ts";
import { ConnectorServicePicker } from "./connector-service-picker.tsx";
import { ContactPicker } from "./contact-picker.tsx";
import { ContextInput } from "./context-input.tsx";
import { DurationPicker } from "./duration-picker.tsx";
import { LocationPicker } from "./location-picker.tsx";
import { PartySizePicker } from "./party-size-picker.tsx";
import { TimeDatePicker } from "./time-date-picker.tsx";
import { TitleInput } from "./title-input.tsx";
import { TriggerWordList } from "./trigger-word-list.tsx";

/** Format a time, dropping :00 minutes for clean display: "6pm" or "5:30pm" */
function formatShortTime(date: Date): string {
  const h = date.getHours() % 12 || 12;
  const m = date.getMinutes();
  const meridiem = date.getHours() >= 12 ? "pm" : "am";
  return m === 0 ? `${h}${meridiem}` : `${h}:${String(m).padStart(2, "0")}${meridiem}`;
}

/** Format ISO ranges into readable text like "during (Mar 31 6pm-8pm, Apr 1 5:30pm-6pm), even if I have conflicts" */
function formatRangesToText(
  ranges: Array<{ startISO: string; endISO: string }>,
  skipCalendarConflicts: boolean,
): string {
  const slots = ranges.map((r) => {
    const start = parseISOToDate(r.startISO);
    const end = parseISOToDate(r.endISO);
    const startDay = format(start, "MMM d");
    const endDay = format(end, "MMM d");
    if (startDay === endDay) {
      return `${startDay} ${formatShortTime(start)}-${formatShortTime(end)}`;
    }
    return `${startDay} ${formatShortTime(start)} - ${endDay} ${formatShortTime(end)}`;
  });
  if (skipCalendarConflicts) {
    return `during (${slots.join(", ")} \u2014 ignore conflicts)`;
  }
  return `during (${slots.join(", ")})`;
}

interface GuidedFlowDropdownProps {
  flow: UseGuidedFlowReturn;
  position: ContactDropdownPosition;
  contextSuggestions: string[];
}

/** See the wheel-lock note in {@link GuidedFlowDropdown} — module-level so attach and detach share one identity. */
function stopWheelPropagation(e: WheelEvent) {
  e.stopPropagation();
}

/**
 * Main dropdown shell for the guided scheduling flow.
 *
 * Portal-rendered to document.body, positioned above the textarea.
 * Renders the appropriate step content based on the current flow step.
 */
export function GuidedFlowDropdown({
  flow,
  position,
  contextSuggestions,
}: GuidedFlowDropdownProps) {
  const { state } = flow;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [availabilityPickerOpen, setAvailabilityPickerOpen] = useState(false);
  const prevStepRef = useRef(state.step);

  // Auto-open calendar picker when navigating to time step with existing constraint ranges
  useEffect(() => {
    if (
      state.step === "time" &&
      prevStepRef.current !== "time" &&
      state.userConstraintRanges?.length
    ) {
      setAvailabilityPickerOpen(true);
    }
    prevStepRef.current = state.step;
  }, [state.step, state.userConstraintRanges]);

  const isGuidedStep = GUIDED_STEPS.includes(state.step);

  // Steps visible for the current flow type
  const visibleSteps = useMemo(
    () => GUIDED_STEPS.filter((s) => !shouldSkipStep(state.flowType, s)),
    [state.flowType],
  );

  const stepLabel = STEP_LABELS[state.step];

  const flowName = state.flowType === "connector" ? "Add a connector" : "Schedule a meeting";
  const dialogLabel = isGuidedStep && stepLabel ? `${flowName}: ${stepLabel}` : flowName;

  // When a modal dialog hosts the omnibox, its scroll lock preventDefault-s
  // wheel events from this body portal (it sits outside the dialog's allowed
  // subtree), so the step list cannot wheel-scroll even though its scrollbar
  // drags fine. The lock listens in the bubble phase on `document`, so
  // stopping propagation here keeps the event from ever reaching it and the
  // browser's native scrolling of this container survives untouched. A native
  // listener via callback ref (not React's synthetic system) guarantees we
  // sit in the event's path ahead of `document`, and re-attaches through the
  // keyed per-step remounts.
  const wheelStopNodeRef = useRef<HTMLDivElement | null>(null);
  const attachWheelStop = useCallback((node: HTMLDivElement | null) => {
    wheelStopNodeRef.current?.removeEventListener("wheel", stopWheelPropagation);
    wheelStopNodeRef.current = node;
    node?.addEventListener("wheel", stopWheelPropagation);
  }, []);

  // Max dropdown height (matches max-h-[268px] on the inner dialog).
  const MAX_HEIGHT = 268;
  // Anchor the dropdown's bottom edge 8px above the textarea, regardless of
  // content height, so all steps appear at the same vertical position.
  const frameTop = position.anchorBottom ? position.anchorBottom - MAX_HEIGHT - 8 : position.top;

  // Focus management: when the step changes to a guided step, ensure something
  // inside the dropdown is focused so Tab/Escape work via handleDropdownKeyDown.
  // Without this, focus falls to document.body when a step picker's input unmounts.
  // biome-ignore lint/correctness/useExhaustiveDependencies: focus only on step change
  useEffect(() => {
    if (!isGuidedStep) return;

    // Defer to let step-specific autoFocus inputs claim focus first.
    // If none do, focus the first focusable child (picker root div or input),
    // falling back to the container itself.
    requestAnimationFrame(() => {
      if (dropdownRef.current && !dropdownRef.current.contains(document.activeElement)) {
        const focusable = Array.from(
          dropdownRef.current.querySelectorAll<HTMLElement>(
            'input, [tabindex="0"], [tabindex="-1"]',
          ),
        ).find((el) => el !== dropdownRef.current);
        (focusable ?? dropdownRef.current).focus({ preventScroll: true });
      }
    });
  }, [state.step]);

  // Close when clicking outside the dropdown.
  // Use `pointerdown` on capture phase and check containment immediately,
  // before React re-renders can unmount the click target (which would cause
  // a false "outside" hit when the user clicks a button that advances the step).
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        flow.dismiss();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [flow.dismiss]);

  // Handle Escape, Tab (skip), and Shift+Tab (back) when focus is inside the dropdown
  const handleDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        flow.dismiss();
      }
      if (e.key === "Tab" && isGuidedStep) {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          // Shift+Tab navigates back to the previous visible step
          const stepIdx = visibleSteps.indexOf(state.step);
          if (stepIdx > 0) {
            flow.jumpToStep(visibleSteps[stepIdx - 1]);
          }
        } else {
          flow.skip();
        }
      }
    },
    [flow.dismiss, isGuidedStep, flow.skip, state.step, flow.jumpToStep, visibleSteps],
  );

  const renderStepContent = () => {
    switch (state.step) {
      case "matching":
        return (
          <TriggerWordList
            matchResults={flow.matchResults}
            selectedIndex={flow.selectedIndex}
            matchInput={flow.matchContext?.word ?? ""}
            onSelect={flow.selectTrigger}
            onHover={flow.setSelectedIndex}
          />
        );

      case "context":
        return (
          <ContextInput
            triggerWord={state.triggerWord ?? ""}
            suggestions={contextSuggestions}
            onSubmit={flow.setContext}
            defaultValue={state.context}
          />
        );

      case "connectorService":
        return (
          <ConnectorServicePicker
            onSelect={flow.setConnectorService}
            defaultValue={state.connectorService}
          />
        );

      case "venue":
        return (
          <ContactPicker
            mode="single"
            onSelect={flow.setVenue}
            defaultValue={state.venue}
            placeholder="Search people or type a name..."
          />
        );

      case "serviceProvider":
        return (
          <ContactPicker
            mode="single"
            onSelect={flow.setServiceProvider}
            defaultValue={state.serviceProvider}
            placeholder="Search people or type a name..."
          />
        );

      case "partySize":
        return <PartySizePicker onSelect={flow.setPartySize} defaultValue={state.partySize} />;

      case "contacts":
        return (
          <ContactPicker
            mode="multi"
            selectedContacts={state.contacts}
            onAdd={flow.addContact}
            onRemove={flow.removeContact}
            onDone={flow.skip}
          />
        );

      case "time":
        return (
          <TimeDatePicker
            onSelect={flow.setTime}
            onPreview={flow.setPreviewTime}
            defaultValue={state.timeDate}
            onPickSpecificTimes={() => setAvailabilityPickerOpen(true)}
          />
        );

      case "duration":
        return <DurationPicker onSelect={flow.setDuration} defaultValue={state.duration} />;

      case "location":
        return (
          <LocationPicker
            onSelect={flow.setLocation}
            onPreview={flow.setPreviewLocation}
            defaultValue={state.location}
          />
        );

      case "title":
        return (
          <TitleInput
            defaultValue={state.title}
            onSubmit={(title) => {
              if (title) flow.setTitle(title);
              else flow.skip();
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <CalendarAvailabilityPicker
        open={availabilityPickerOpen}
        onOpenChange={setAvailabilityPickerOpen}
        initialDate={new Date()}
        duration={30}
        initialRanges={state.userConstraintRanges ?? undefined}
        initialSkipCalendarConflicts={state.skipCalendarConflicts ?? undefined}
        onSave={(ranges, skipCalendarConflicts) => {
          const text = formatRangesToText(ranges, skipCalendarConflicts);
          flow.setTimeWithRanges(text, ranges, skipCalendarConflicts);
          setAvailabilityPickerOpen(false);
        }}
      />
      {!availabilityPickerOpen &&
        createPortal(
          <div
            // Marks this body portal so host popovers/dialogs (e.g. the range
            // selection AgentPopover) don't treat clicks here as "outside" and
            // dismiss — letting the picker's search input actually take focus.
            data-typeahead-portal=""
            style={{
              position: "absolute",
              top: frameTop,
              left: position.left,
              width: 300,
              height: MAX_HEIGHT,
              pointerEvents: "none",
            }}
            className="z-50 flex flex-col justify-end"
          >
            <div
              key={state.step}
              ref={dropdownRef}
              role="dialog"
              aria-label={dialogLabel}
              tabIndex={-1}
              onKeyDown={handleDropdownKeyDown}
              className={cn(
                "pointer-events-auto",
                "flex flex-col min-w-0 max-w-[calc(100vw-2rem)] max-h-[268px] overflow-hidden outline-none",
                "bg-popover text-popover-foreground rounded-xl shadow-lg border border-border",
                "animate-in fade-in-0 slide-in-from-bottom-1 duration-150",
                isGuidedStep && "h-full",
              )}
            >
              {/* Matching step header: title + close button */}
              {state.step === "matching" && (
                <div className="flex items-center justify-between px-3 pt-2.5 pb-1 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
                      Schedule
                    </span>
                    <span className="text-xs text-muted-foreground">tab to accept</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="action"
                    data-statsig-name="scheduling-typeahead-close"
                    aria-label="Close"
                    className="size-5 text-muted-foreground"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      flow.dismiss();
                    }}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              )}

              {/* Guided step header: Title (left) | Back + Skip (right) */}
              {isGuidedStep &&
                stepLabel &&
                (() => {
                  const stepIdx = visibleSteps.indexOf(state.step);
                  const isFirst = stepIdx === 0;
                  const isLast = stepIdx === visibleSteps.length - 1;
                  return (
                    <div className="flex items-center justify-between px-3 pt-3 pb-1 shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
                          {stepLabel}
                        </span>
                        <span className="text-xs text-muted-foreground">tab to accept</span>
                      </div>
                      <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
                        {!isFirst && (
                          <Button
                            variant="ghost"
                            size="2xs"
                            data-statsig-name="scheduling-typeahead-prev-step"
                            aria-label="Previous step"
                            className="gap-0.5 px-1 h-5 text-muted-foreground"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              flow.jumpToStep(visibleSteps[stepIdx - 1]);
                            }}
                          >
                            <ChevronLeft className="size-3" />
                            Prev
                          </Button>
                        )}
                        {!isFirst && !isLast && <span className="text-muted-foreground/60">|</span>}
                        {!isLast && (
                          <Button
                            variant="ghost"
                            size="2xs"
                            data-statsig-name="scheduling-typeahead-next-step"
                            aria-label="Next step"
                            className="gap-0.5 px-1 h-5 text-muted-foreground"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              flow.skip();
                            }}
                          >
                            Next
                            <ChevronRight className="size-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })()}

              {/* Scrollable step content */}
              <div
                ref={attachWheelStop}
                className={cn(
                  "relative min-h-0 overflow-y-auto",
                  isGuidedStep && "flex-1",
                  "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent hover:scrollbar-thumb-border",
                )}
              >
                {renderStepContent()}
                {/* Fade overlay — sticky to bottom, inset from border and scrollbar */}
                <div className="sticky bottom-0 h-3 -mt-3 pointer-events-none bg-gradient-to-t from-popover to-transparent" />
              </div>

              {/* Solid spacer so scrollbar clears rounded corners */}
              <div className="shrink-0 h-3 bg-popover" />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
